import { CircularProgress, Divider, Typography } from '@material-ui/core';
import { UnlockableContentType } from '@mogul/api-client';
import { resolveSigningMessage, SignType } from '@mogul/shared';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAccount, useNetwork, useSignMessage } from 'wagmi';

import { CHAIN_ID_TO_TYPE_MAP, WEB3_CONFIG } from '../../../../lib';
import { getUnlockableContent } from '../../../../utils/collection.util';
import { Button } from '../../../core/button';
import { Panel } from '../../panel/Panel.component';
import styles from './UnlockableContentModal.module.css';

interface UnlockableContentModalProps extends ModalProps {
  tokenAddress: string;
  tokenId: string;
}

interface ModalProps {
  hideModal: () => void;
}

const UnlockableContent: FC<UnlockableContentModalProps> = ({
  tokenAddress,
  tokenId,
  hideModal,
}) => {
  const { address } = useAccount();
  const { chain } = useNetwork();
  const { signMessageAsync } = useSignMessage();
  const [isLoading, setIsLoading] = useState(true);
  const [content, setContent] = useState('');
  const [contentType, setContentType] = useState<UnlockableContentType>(
    UnlockableContentType.Markdown,
  );

  useEffect(() => {
    if (!address) {
      return;
    }

    void (async () => {
      const signedMsg = await signMessageAsync({
        message: resolveSigningMessage(SignType.unlockContent),
      });

      const { content: newContent, type } = await getUnlockableContent(
        tokenAddress,
        tokenId,
        address,
        signedMsg,
        CHAIN_ID_TO_TYPE_MAP[chain?.id ?? WEB3_CONFIG.chains.bsc.id],
      );

      if (!newContent) {
        setContent('Access Denied.');
      } else {
        setContent(newContent);
        setContentType(type);
      }

      setIsLoading(false);
    })();
  }, [address, signMessageAsync, chain?.id]);

  return (
    <Panel
      className={`${styles.panel} overflow-hidden rounded`}
      bodyClassName={`${styles.panelBody} flex flex-col rounded-lg p-0 m-1`}
    >
      <Typography variant="h1" style={{ fontSize: 32, color: 'gold' }}>
        Unlocked Content
      </Typography>
      <Divider
        className="w-full"
        style={{ background: 'gold', margin: '25px 0' }}
      />
      <div className="w-full grid place-items-center border border-solid border-white rounded py-5">
        {isLoading ? (
          <CircularProgress className={`${styles.loader} mb-5`} size={85} />
        ) : (
          <div className="w-11/12">
            <pre className={styles.contentFormat}>
              {/* https://github.com/remarkjs/react-markdown, uses this standard: https://commonmark.org/help/ */}
              {contentType === UnlockableContentType.Markdown && (
                <ReactMarkdown className={styles.markdownContainer}>
                  {content}
                </ReactMarkdown>
              )}
              {contentType === UnlockableContentType.Image && (
                <img
                  src={content}
                  alt="Unlockable Content"
                  className="w-full"
                />
              )}
              {contentType === UnlockableContentType.Video && (
                <video controls className="w-full">
                  <source src={content} />
                  <track kind="captions" />
                </video>
              )}
              {contentType === UnlockableContentType.Audio && (
                <audio controls className="w-full">
                  <source src={content} />
                  <track kind="captions" />
                </audio>
              )}
            </pre>
          </div>
        )}
      </div>
      <Divider
        className="w-full"
        style={{ background: 'gold', margin: '25px 0' }}
      />
      <Button onClick={hideModal}>Close</Button>
    </Panel>
  );
};

export const UnlockableContentModal =
  ({ tokenAddress, tokenId }: { tokenAddress: string; tokenId: string }) =>
  ({ hideModal }: ModalProps) => {
    return (
      <UnlockableContent
        tokenAddress={tokenAddress}
        tokenId={tokenId}
        hideModal={hideModal}
      />
    );
  };
